import PropTypes from 'prop-types';
import React from 'react';
import Combobox from '~/components/Combobox';
import useAuthentication from '~/hooks/use-authentication';
import useTagsSearch from '~/hooks/use-tags-search';

function tagsToComboboxItems(tags) {
  return tags?.filter(Boolean).map(tag => {
    if (typeof tag === 'string') {
      return { value: tag, label: tag, indeterminate: false };
    }
    return {
      label: tag.name || tag.label,
      value: tag.id || tag.value,
      indeterminate: tag.indeterminate,
    };
  });
}

function ComboboxItemToTag(item) {
  if (typeof item === 'object' && item.value && item.label) {
    return { name: item.label, id: item.value, indeterminate: item.indeterminate };
  }
  if (typeof item === 'string') {
    return { name: item, id: item, indeterminate: item.indeterminate };
  }
  return null;
}

function ComboboxItemsToTags(items) {
  if (!items) return [];
  if (!Array.isArray(items)) return ComboboxItemToTag(items);
  const returnTags = [];
  items.forEach(item => {
    const tag = ComboboxItemToTag(item);
    if (tag) returnTags.push(tag);
  });

  return returnTags;
}

function TagsSelector({
  width,
  label,
  error,
  helpText,
  size,
  multiple = true,
  organizationId,
  defaultSelected = [],
  placeholder,
  maxVisibleTags,
  onChange,
  disabledForMember,
  tagResource = 'tags',
}) {
  const tagsSearch = useTagsSearch(tagResource, organizationId);
  const { isOrgMember } = useAuthentication();
  const isDisabled = disabledForMember && isOrgMember;

  const onChangeSelected = items => {
    if (multiple) return onChange(ComboboxItemsToTags(items));
    return onChange(ComboboxItemToTag(items));
  };

  return (
    <Combobox
      width={width}
      label={label}
      errorText={error}
      helpText={helpText}
      items={tagsSearch.items}
      placeholder={placeholder}
      disabled={isDisabled}
      multiple={multiple}
      size={size}
      defaultSelected={tagsToComboboxItems(defaultSelected)}
      onSelectedChange={onChangeSelected}
      maxVisibleItems={maxVisibleTags}
      search={tagsSearch.search}
      onSearchChange={tagsSearch.setSearch}
      startPaging={tagsSearch.startPaging}
      loading={tagsSearch.loading}
    />
  );
}

TagsSelector.propTypes = {
  width: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.string,
  helpText: PropTypes.string,
  size: PropTypes.oneOf(['1', '2', '3']),
  multiple: PropTypes.bool,
  organizationId: PropTypes.string,
  defaultSelected: PropTypes.array,
  placeholder: PropTypes.string,
  maxVisibleTags: PropTypes.number,
  onChange: PropTypes.func,
  disabledForMember: PropTypes.bool,
  tagResource: PropTypes.string,
};

export default TagsSelector;
